import { useState, useEffect, useRef, useCallback } from 'react';

const API = 'https://bot.devit.es/api/xml';

function api(path, opts = {}) {
  const token = localStorage.getItem('devit_token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  }).then(r => r.json());
}

const CAT_LABELS = { venta: 'Venta', alquiler: 'Alquiler' };
const CAT_COLORS = { venta: '#a78bfa', alquiler: '#34d399' };

export default function Propiedades() {
  const [status, setStatus]         = useState(null);
  const [feedUrl, setFeedUrl]       = useState('');
  const [savingUrl, setSavingUrl]   = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [props, setProps]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [filterCat, setFilterCat]   = useState('');
  const [filterZona, setFilterZona] = useState('');
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('feed');
  const [modoSync, setModoSync]     = useState('suma');
  const fileRef = useRef();
  const pollRef = useRef(null);

  const loadStatus = useCallback(() =>
    api('/status').then(d => {
      setStatus(d);
      setFeedUrl(d.xml_feed_url || '');
      // Si hay un sync en curso detectado desde el servidor, activar polling
      if (d.sync_estado === 'en_curso') startPolling();
    }), []);

  const loadProps = useCallback((p = 1, cat = filterCat, zona = filterZona) => {
    let url = `/propiedades?page=${p}&limit=50`;
    if (cat)  url += `&categoria=${cat}`;
    if (zona) url += `&zona=${encodeURIComponent(zona)}`;
    api(url).then(d => { setProps(d.items || []); setTotal(d.total || 0); });
  }, [filterCat, filterZona]);

  useEffect(() => {
    loadStatus().finally(() => setLoading(false));
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (tab === 'propiedades') loadProps(1);
  }, [tab, filterCat, filterZona]);

  // ─── Polling del estado del sync background ───────────────────────────────
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const estado = await api('/sync/estado');
      if (estado.status === 'en_curso') return; // seguir esperando

      stopPolling();
      setSyncing(false);

      if (estado.status === 'ok') {
        setSyncResult({ status: 'ok', ...estado.stats });
      } else if (estado.status === 'error') {
        setSyncResult({ status: 'error', detail: estado.error });
      }
      await loadStatus();
      if (tab === 'propiedades') loadProps(1);
    }, 2000); // poll cada 2 segundos
  };

  // ─── Acciones ─────────────────────────────────────────────────────────────
  const handleSaveUrl = async () => {
    setSavingUrl(true);
    await api('/feed', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xml_feed_url: feedUrl || null }),
    });
    await loadStatus();
    setSavingUrl(false);
  };

  const iniciarSync = (resultado) => {
    if (resultado.status === 'en_curso' || resultado.status === 'iniciado') {
      setSyncing(true);
      setSyncResult(null);
      startPolling();
    } else {
      setSyncResult({ status: 'error', detail: resultado.mensaje || 'Error desconocido' });
    }
  };

  const handleSyncUrl = async () => {
    const res = await api(`/sync/url?modo=${modoSync}`, { method: 'POST' });
    iniciarSync(res);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('devit_token');
    const res = await fetch(`${API}/sync/upload?modo=${modoSync}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    }).then(r => r.json());
    iniciarSync(res);
    fileRef.current.value = '';
  };

  const handleDelete = async (id) => {
    await api(`/propiedades/${id}`, { method: 'DELETE' });
    loadProps(page, filterCat, filterZona);
    loadStatus();
  };

  if (loading) return <div className="content" style={{ color:'var(--text3)' }}>Cargando...</div>;

  const totalPages = Math.ceil(total / 50);

  return (
    <div>
      <div className="topbar">
        <div>
          <div className="page-title">Propiedades</div>
          <div className="page-sub">Sincronización de catálogo inmobiliario</div>
        </div>
      </div>

      <div className="content">

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'12px', marginBottom:'20px' }}>
          <StatCard label="Propiedades activas" value={status?.total_activas ?? 0} icon="🏠" />
          {status?.por_categoria && Object.entries(status.por_categoria).map(([cat, n]) => (
            <StatCard key={cat} label={CAT_LABELS[cat]||cat} value={n} icon="📦" accent={CAT_COLORS[cat]} />
          ))}
          <StatCard label="Último sync"
            value={status?.ultimo_sync
              ? new Date(status.ultimo_sync).toLocaleString('es-ES',{dateStyle:'short',timeStyle:'short'})
              : '—'} icon="🔄" />
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:'4px', marginBottom:'20px', borderBottom:'1px solid var(--border)' }}>
          {[['feed','⚙️ Configuración'],['propiedades','🏘️ Listado']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              padding:'8px 18px', background:'none', border:'none',
              borderBottom: tab===id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab===id ? 'var(--accent)' : 'var(--text3)',
              fontFamily:'inherit', fontSize:'13px', fontWeight: tab===id ? 600 : 400,
              cursor:'pointer', marginBottom:'-1px',
            }}>{label}</button>
          ))}
        </div>

        {/* Tab Feed */}
        {tab === 'feed' && (
          <>
            <div className="card" style={{ marginBottom:'16px' }}>
              <div className="card-title">URL del feed XML</div>
              <p style={{ color:'var(--text3)', fontSize:'13px', marginBottom:'14px' }}>
                Kyero, Idealista, Fotocasa o cualquier formato XML inmobiliario.
              </p>
              <div style={{ display:'flex', gap:'10px' }}>
                <input className="form-input" style={{ flex:1 }}
                  placeholder="https://www.kyero.com/feed/your-id.xml"
                  value={feedUrl} onChange={e => setFeedUrl(e.target.value)} />
                <button className="btn btn-ghost" onClick={handleSaveUrl} disabled={savingUrl}>
                  {savingUrl ? 'Guardando…' : '💾 Guardar URL'}
                </button>
              </div>
            </div>

            {/* Modo */}
            <div className="card" style={{ marginBottom:'16px' }}>
              <div className="card-title">Modo de sincronización</div>
              <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                {[
                  { id:'suma',   icon:'➕', titulo:'Modo Suma',   desc:'Solo añade y actualiza. No elimina nada. Para subidas parciales y pruebas.' },
                  { id:'espejo', icon:'🪞', titulo:'Modo Espejo', desc:'El XML es la fuente de verdad. Desactiva lo que ya no aparece. Para feeds completos automáticos.' },
                ].map(m => (
                  <div key={m.id} onClick={() => setModoSync(m.id)} style={{
                    flex:'1', minWidth:'200px', cursor:'pointer', borderRadius:'8px',
                    padding:'14px 16px',
                    border:`2px solid ${modoSync===m.id ? 'var(--accent)' : 'var(--border)'}`,
                    background: modoSync===m.id ? 'var(--accent)11' : 'var(--bg2)',
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      <span style={{ fontSize:'18px' }}>{m.icon}</span>
                      <span style={{ fontWeight:600, color: modoSync===m.id ? 'var(--accent)' : 'var(--text1)' }}>{m.titulo}</span>
                      {modoSync===m.id && <span style={{ marginLeft:'auto', fontSize:'11px', color:'var(--accent)' }}>● Activo</span>}
                    </div>
                    <p style={{ color:'var(--text3)', fontSize:'12px', margin:0, lineHeight:'1.5' }}>{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Sync */}
            <div className="card" style={{ marginBottom:'16px' }}>
              <div className="card-title">Sincronizar ahora</div>

              {modoSync === 'espejo' && (
                <div style={{
                  background:'#7f1d1d22', border:'1px solid #f8717144', borderRadius:'6px',
                  padding:'10px 14px', marginBottom:'14px', fontSize:'12px', color:'#fca5a5',
                }}>
                  ⚠️ <strong>Modo Espejo:</strong> las propiedades que no estén en el XML quedarán desactivadas.
                </div>
              )}

              {syncing && <SyncProgress />}

              {!syncing && (
                <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                  <button className="btn btn-primary"
                    onClick={handleSyncUrl} disabled={!status?.xml_feed_url}>
                    🔄 Sync desde URL
                  </button>
                  <div>
                    <input ref={fileRef} type="file" accept=".xml,.txt"
                      style={{ display:'none' }} onChange={handleUpload} />
                    <button className="btn btn-ghost" onClick={() => fileRef.current.click()}>
                      📂 Subir archivo XML
                    </button>
                  </div>
                </div>
              )}

              {!status?.xml_feed_url && !syncing && (
                <p style={{ color:'var(--text3)', fontSize:'12px', marginTop:'8px' }}>
                  ℹ️ Configura una URL para habilitar el sync desde URL.
                </p>
              )}

              {syncResult && !syncing && <SyncResultCard result={syncResult} />}
            </div>

            <div className="card">
              <div className="card-title">⏰ Sincronización automática nocturna</div>
              <p style={{ color:'var(--text3)', fontSize:'13px', margin:0 }}>
                Si tienes una URL configurada, el sistema sincroniza cada noche a las 3:00 AM en modo Espejo.
              </p>
              <div style={{ marginTop:'10px', fontSize:'12px', color: status?.xml_feed_url ? '#34d399' : 'var(--text3)' }}>
                {status?.xml_feed_url
                  ? `✅ Auto-sync activo — ${status.xml_feed_url}`
                  : '⬜ Auto-sync inactivo — configura una URL para activarlo'}
              </div>
            </div>
          </>
        )}

        {/* Tab Propiedades */}
        {tab === 'propiedades' && (
          <>
            <div style={{ display:'flex', gap:'10px', marginBottom:'14px', flexWrap:'wrap' }}>
              <select className="form-input" style={{ width:'auto', minWidth:'150px' }}
                value={filterCat} onChange={e => { setFilterCat(e.target.value); setPage(1); }}>
                <option value="">Todas las categorías</option>
                <option value="venta">Venta</option>
                <option value="alquiler">Alquiler</option>
              </select>
              <input className="form-input" style={{ width:'200px' }}
                placeholder="Filtrar por zona…"
                value={filterZona} onChange={e => { setFilterZona(e.target.value); setPage(1); }} />
              <span style={{ color:'var(--text3)', fontSize:'13px', alignSelf:'center' }}>{total} propiedades</span>
            </div>

            {props.length === 0 ? (
              <div className="card" style={{ textAlign:'center', color:'var(--text3)', padding:'40px' }}>
                No hay propiedades activas. Sincroniza tu feed XML para empezar.
              </div>
            ) : (
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)' }}>
                      {['Tipo','Categoría','Zona','Hab.','m²','Precio','Foto',''].map(h => (
                        <th key={h} style={{ padding:'10px 14px', textAlign:'left', color:'var(--text3)', fontWeight:500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {props.map(p => (
                      <tr key={p.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'10px 14px', color:'var(--text1)' }}>{p.tipo}</td>
                        <td style={{ padding:'10px 14px' }}>
                          <span style={{
                            background:(CAT_COLORS[p.categoria]||'#888')+'22',
                            color: CAT_COLORS[p.categoria]||'var(--text2)',
                            borderRadius:'4px', padding:'2px 8px', fontSize:'11px', fontWeight:600,
                          }}>{CAT_LABELS[p.categoria]||p.categoria}</span>
                        </td>
                        <td style={{ padding:'10px 14px', color:'var(--text2)' }}>{p.zona}</td>
                        <td style={{ padding:'10px 14px', color:'var(--text2)' }}>{p.habitaciones ?? '—'}</td>
                        <td style={{ padding:'10px 14px', color:'var(--text2)' }}>
                          {p.metros_cuadrados ? `${Math.round(p.metros_cuadrados)} m²` : '—'}
                        </td>
                        <td style={{ padding:'10px 14px', color:'var(--text1)', fontWeight:500, whiteSpace:'nowrap' }}>
                          {p.precio_venta    ? `${p.precio_venta.toLocaleString('es-ES')} €` :
                           p.precio_alquiler ? `${p.precio_alquiler.toLocaleString('es-ES')} €/mes` : '—'}
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          {p.foto_url
                            ? <img src={p.foto_url} alt="" style={{ width:'48px', height:'36px', objectFit:'cover', borderRadius:'4px' }}
                                onError={e => { e.target.style.display='none'; }} />
                            : <span style={{ color:'var(--text3)', fontSize:'11px' }}>—</span>}
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          <div style={{ display:'flex', gap:'6px' }}>
                            {p.url_ficha && (
                              <a href={p.url_ficha} target="_blank" rel="noreferrer"
                                style={{ color:'var(--accent)', fontSize:'11px', textDecoration:'none' }}>Ver ↗</a>
                            )}
                            <button onClick={() => handleDelete(p.id)}
                              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:'11px' }}
                              title="Desactivar">✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div style={{ display:'flex', gap:'8px', justifyContent:'center', marginTop:'16px' }}>
                <button className="btn btn-ghost" disabled={page<=1}
                  onClick={() => { const p=page-1; setPage(p); loadProps(p); }}>← Anterior</button>
                <span style={{ color:'var(--text3)', fontSize:'13px', alignSelf:'center' }}>{page}/{totalPages}</span>
                <button className="btn btn-ghost" disabled={page>=totalPages}
                  onClick={() => { const p=page+1; setPage(p); loadProps(p); }}>Siguiente →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div className="card" style={{ padding:'16px 20px' }}>
      <div style={{ fontSize:'22px', marginBottom:'6px' }}>{icon}</div>
      <div style={{ fontSize:'24px', fontWeight:700, color: accent||'var(--text1)' }}>{value}</div>
      <div style={{ fontSize:'12px', color:'var(--text3)', marginTop:'2px' }}>{label}</div>
    </div>
  );
}

function SyncProgress() {
  const [dots, setDots] = useState('.');
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? '.' : d + '.'), 500);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      background:'var(--bg2)', border:'1px solid var(--border)',
      borderRadius:'8px', padding:'16px 20px', marginBottom:'14px',
      display:'flex', alignItems:'center', gap:'12px',
    }}>
      <Spinner />
      <div>
        <div style={{ color:'var(--text1)', fontWeight:600, fontSize:'14px' }}>
          Sincronizando{dots}
        </div>
        <div style={{ color:'var(--text3)', fontSize:'12px', marginTop:'2px' }}>
          Procesando propiedades en background — puedes seguir usando el dashboard
        </div>
      </div>
    </div>
  );
}

function SyncResultCard({ result }) {
  const ok = result.status === 'ok';
  return (
    <div style={{
      marginTop:'16px',
      background: ok ? '#064e3b22' : '#7f1d1d22',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}44`,
      borderRadius:'8px', padding:'14px 18px',
    }}>
      <div style={{ fontWeight:600, color: ok ? '#34d399' : '#f87171', marginBottom:'8px' }}>
        {ok ? `✅ Sync completado — modo ${result.modo||''}` : '❌ Error en la sincronización'}
      </div>
      {ok ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,auto)', gap:'16px', fontSize:'13px' }}>
          {[['Insertadas',result.insertadas,'#34d399'],['Actualizadas',result.actualizadas,'#60a5fa'],
            ['Desactivadas',result.desactivadas,'#f59e0b'],['Total activas',result.total_activas,'var(--text1)']
          ].map(([l,v,c]) => (
            <div key={l}>
              <div style={{ color:c, fontWeight:700, fontSize:'20px' }}>{v}</div>
              <div style={{ color:'var(--text3)' }}>{l}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color:'var(--text3)', fontSize:'13px' }}>{result.detail||'Error desconocido'}</div>
      )}
      {result.errores?.length > 0 && (
        <details style={{ marginTop:'10px' }}>
          <summary style={{ color:'var(--text3)', fontSize:'12px', cursor:'pointer' }}>
            {result.errores.length} advertencias
          </summary>
          <ul style={{ color:'var(--text3)', fontSize:'11px', marginTop:'6px', paddingLeft:'16px' }}>
            {result.errores.slice(0,10).map((e,i) => <li key={i}>{e}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function Spinner() {
  return <span style={{
    display:'inline-block', width:'16px', height:'16px',
    border:'2px solid var(--accent)', borderTopColor:'transparent',
    borderRadius:'50%', animation:'spin 0.7s linear infinite', flexShrink:0,
  }} />;
}